import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Ban,
  CalendarDays,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Loader2,
  Move,
} from 'lucide-react'

import { WalkinQueuePanel } from '@/components/calendar/WalkinQueuePanel'
import { AppointmentDetailModal } from '@/components/calendar/AppointmentDetailModal'
import { AppointmentLookupPanel } from '@/components/calendar/AppointmentLookupPanel'
import { AppointmentOutcomeModal } from '@/components/calendar/AppointmentOutcomeModal'
import { BlockoutManager } from '@/components/calendar/BlockoutManager'
import { BookAppointmentModal } from '@/components/calendar/BookAppointmentModal'
import { ChairBoardGrid } from '@/components/calendar/ChairBoardGrid'
import { CheckInArrivalsPanel } from '@/components/calendar/CheckInArrivalsPanel'
import { OperationsSettingsPanel } from '@/components/calendar/OperationsSettingsPanel'
import { DayCalendar } from '@/components/calendar/DayCalendar'
import { WeekCalendar } from '@/components/calendar/WeekCalendar'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  fetchAppointmentSlotConfig,
  fetchAppointments,
  approveOnlineAppointment,
  rescheduleAppointment,
} from '@/lib/api/appointments'
import { fetchChairs } from '@/lib/api/chairs'
import { fetchServices } from '@/lib/api/services'
import { fetchStylists } from '@/lib/api/stylists'
import { fetchWalkinQueue } from '@/lib/api/walkinQueue'
import {
  formatDayHeading,
  nextIsoDate,
  shiftIsoDate,
  todayIso,
} from '@/lib/calendar/calendarDates'
import { dayKeyFromDate } from '@/lib/calendar/dayCalendar'
import {
  buildWeekDayRange,
  formatWeekHeading,
  resolveWeekGridHours,
} from '@/lib/calendar/weekCalendar'
import { normalizeBusinessHours } from '@/lib/businessHours'
import { checkInArrivalsForDate } from '@/lib/calendar/checkIn'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'

/** @typedef {'day' | 'week'} CalendarViewMode */
/** @typedef {'floor' | 'schedule' | 'blocks'} CalendarOpsView */

const OPS_VIEWS = [
  {
    id: /** @type {CalendarOpsView} */ ('floor'),
    label: 'Floor',
    icon: LayoutGrid,
    description: 'Live chairs, check-ins, and walk-in queue.',
  },
  {
    id: /** @type {CalendarOpsView} */ ('schedule'),
    label: 'Schedule',
    icon: CalendarDays,
    description: 'Day or week grid — click a booking for details.',
  },
  {
    id: /** @type {CalendarOpsView} */ ('blocks'),
    label: 'Blocks',
    icon: Ban,
    description: 'Lunch, training, holidays, and chair/stylist blocks.',
  },
]

export function CalendarPage() {
  const { activeBranchId, activeBranch } = useBranch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [opsView, setOpsView] = useState(/** @type {CalendarOpsView} */ ('floor'))
  const [viewMode, setViewMode] = useState(/** @type {CalendarViewMode} */ ('day'))
  const [selectedDate, setSelectedDate] = useState(todayIso)
  const [appointments, setAppointments] = useState([])
  const [stylists, setStylists] = useState([])
  const [chairs, setChairs] = useState([])
  const [slotMinutes, setSlotMinutes] = useState(15)
  const [queueEntries, setQueueEntries] = useState([])
  const [queueServices, setQueueServices] = useState([])
  const [metaLoading, setMetaLoading] = useState(true)
  const [appointmentsLoading, setAppointmentsLoading] = useState(true)
  const [queueLoading, setQueueLoading] = useState(true)
  const [reschedulingId, setReschedulingId] = useState(null)
  const [boardRefreshKey, setBoardRefreshKey] = useState(0)
  const [bookModalOpen, setBookModalOpen] = useState(false)
  const [selectedAppointment, setSelectedAppointment] = useState(null)
  const [rescheduleMode, setRescheduleMode] = useState(false)
  const [outcomeAppointment, setOutcomeAppointment] = useState(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const businessHours = useMemo(
    () => normalizeBusinessHours(activeBranch?.business_hours),
    [activeBranch?.business_hours],
  )

  const weekRange = useMemo(() => buildWeekDayRange(selectedDate), [selectedDate])
  const dayHours = businessHours[dayKeyFromDate(selectedDate)] ?? { open: '10:00', close: '22:00' }
  const weekGridHours = useMemo(
    () => resolveWeekGridHours(businessHours, weekRange.dates),
    [businessHours, weekRange.dates],
  )

  const appointmentRange = useMemo(
    () =>
      viewMode === 'week'
        ? { from: weekRange.from, to: weekRange.to }
        : { from: selectedDate, to: nextIsoDate(selectedDate) },
    [selectedDate, viewMode, weekRange.from, weekRange.to],
  )

  const refreshQueue = useCallback(async () => {
    if (!activeBranchId) {
      return
    }

    setQueueLoading(true)

    try {
      const entries = await fetchWalkinQueue()
      setQueueEntries(entries)
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load walk-in queue.'))
    } finally {
      setQueueLoading(false)
    }
  }, [activeBranchId])

  useEffect(() => {
    if (!activeBranchId) {
      return
    }

    let cancelled = false
    setMetaLoading(true)
    setError('')

    async function loadMeta() {
      const results = await Promise.allSettled([
        fetchStylists(),
        fetchAppointmentSlotConfig(),
        fetchServices(),
        fetchChairs(),
      ])

      if (cancelled) {
        return
      }

      const [stylistResult, slotResult, serviceResult, chairResult] = results
      const failures = []

      if (stylistResult.status === 'fulfilled') {
        setStylists(stylistResult.value)
      } else {
        failures.push('stylists')
      }

      if (slotResult.status === 'fulfilled') {
        setSlotMinutes(slotResult.value.slot_minutes ?? 15)
      } else {
        failures.push('slot settings')
      }

      if (serviceResult.status === 'fulfilled') {
        setQueueServices(serviceResult.value.filter((service) => service.is_active))
      } else {
        failures.push('services')
      }

      if (chairResult.status === 'fulfilled') {
        setChairs(chairResult.value)
      } else {
        failures.push('chairs')
      }

      if (failures.length > 0) {
        const rejected = results.find((result) => result.status === 'rejected')
        setError(apiErrorMessage(
          rejected?.reason,
          `Unable to load calendar settings (${failures.join(', ')}).`,
        ))
      }
    }

    loadMeta().finally(() => {
      if (!cancelled) {
        setMetaLoading(false)
      }
    })
    refreshQueue()

    return () => {
      cancelled = true
    }
  }, [activeBranchId, refreshQueue])

  useEffect(() => {
    if (!activeBranchId || opsView !== 'floor') {
      return undefined
    }

    const timer = window.setInterval(() => {
      refreshQueue()
    }, 30000)

    return () => window.clearInterval(timer)
  }, [activeBranchId, opsView, refreshQueue])

  useEffect(() => {
    if (!activeBranchId) {
      return
    }

    let cancelled = false
    setAppointmentsLoading(true)
    setError('')

    async function loadAppointments() {
      try {
        const appointmentItems = await fetchAppointments(appointmentRange)

        if (!cancelled) {
          setAppointments(appointmentItems)
        }
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, 'Unable to load calendar.'))
        }
      } finally {
        if (!cancelled) {
          setAppointmentsLoading(false)
        }
      }
    }

    loadAppointments()

    return () => {
      cancelled = true
    }
  }, [activeBranchId, appointmentRange])

  useEffect(() => {
    const shouldBook = searchParams.get('book') === '1'
    const appointmentId = searchParams.get('appointment')

    if (shouldBook) {
      setBookModalOpen(true)
      setOpsView('schedule')
    }

    if (appointmentId && appointments.length > 0) {
      const match = appointments.find((item) => String(item.id) === appointmentId)
      if (match) {
        setSelectedAppointment(match)
        setOpsView('schedule')
      }
    }

    if (shouldBook || appointmentId) {
      const next = new URLSearchParams(searchParams)
      next.delete('book')
      next.delete('appointment')
      setSearchParams(next, { replace: true })
    }
  }, [appointments, searchParams, setSearchParams])

  /**
   * @param {number} appointmentId
   * @param {{ starts_at: string, stylist_id?: number | null }} payload
   */
  async function handleReschedule(appointmentId, payload) {
    setReschedulingId(appointmentId)
    setError('')
    setSuccess('')

    try {
      const updated = await rescheduleAppointment(appointmentId, payload)
      setAppointments((current) =>
        current.map((appointment) =>
          appointment.id === updated.id
            ? {
                ...appointment,
                ...updated,
                customer: updated.customer ?? appointment.customer,
                services: updated.services ?? appointment.services,
              }
            : appointment,
        ),
      )
      setSuccess('Appointment rescheduled.')
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to reschedule appointment.')
      throw err
    } finally {
      setReschedulingId(null)
    }
  }

  const bookingCount = appointments.filter(
    (appointment) => !['cancelled', 'no_show'].includes(appointment.status),
  ).length

  const arrivalsDate = viewMode === 'day' ? selectedDate : todayIso()
  const arrivalsCount = useMemo(
    () => checkInArrivalsForDate(appointments, arrivalsDate).length,
    [appointments, arrivalsDate],
  )

  const activeOpsView = OPS_VIEWS.find((view) => view.id === opsView) ?? OPS_VIEWS[0]

  const heading =
    viewMode === 'week' ? formatWeekHeading(weekRange.dates) : formatDayHeading(selectedDate)

  const navStep = viewMode === 'week' ? 7 : 1

  /**
   * @param {string} dayDate
   */
  function openDayView(dayDate) {
    setSelectedDate(dayDate)
    setViewMode('day')
  }

  /**
   * @param {string} dayDate
   */
  function handleLookupDateSelect(dayDate) {
    setSelectedDate(dayDate)
    setViewMode('day')
    setOpsView('schedule')
  }

  /**
   * @param {import('@/types/api').Appointment} appointment
   */
  function handleLookupAppointmentSelect(appointment) {
    handleLookupDateSelect(appointment.starts_at.slice(0, 10))
    setSelectedAppointment(appointment)
  }

  /**
   * @param {{ appointments: import('@/types/api').Appointment[] }} result
   */
  async function handleBooked(result) {
    setSuccess(
      result.appointments.length > 1
        ? `${result.appointments.length} recurring appointments booked.`
        : 'Appointment booked.',
    )

    const appointmentItems = await fetchAppointments(appointmentRange)
    setAppointments(appointmentItems)
  }

  async function handleBoardUpdated() {
    await refreshQueue()
  }

  async function handleCheckInUpdated() {
    setSuccess('Guest status updated.')
    setError('')

    try {
      const appointmentItems = await fetchAppointments(appointmentRange)
      setAppointments(appointmentItems)
      setBoardRefreshKey((current) => current + 1)
      await refreshQueue()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to refresh calendar after check-in.'))
    }
  }

  async function handleOnlineApproved() {
    try {
      const appointmentItems = await fetchAppointments(appointmentRange)
      setAppointments(appointmentItems)
      setSuccess('Online booking approved and added to the schedule.')
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to refresh calendar.'))
    }
  }

  async function handleOutcomeUpdated() {
    setSuccess('Appointment updated.')
    setError('')

    try {
      const appointmentItems = await fetchAppointments(appointmentRange)
      setAppointments(appointmentItems)
      setBoardRefreshKey((current) => current + 1)
      await refreshQueue()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to refresh calendar.'))
    }
  }

  const activeStylists = useMemo(
    () => stylists.filter((stylist) => stylist.is_active),
    [stylists],
  )

  const calendarReady = !metaLoading && activeStylists.length > 0

  return (
    <div className="mx-auto max-w-[1440px] space-y-5 px-4 py-6 pb-10 lg:px-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2 text-primary">
            <CalendarDays className="h-5 w-5" />
            <span className="text-xs font-bold uppercase tracking-widest">Operations</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Calendar & floor</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {activeOpsView.description}{' '}
            {opsView === 'schedule' ? (
              <>
                {bookingCount} active booking{bookingCount === 1 ? '' : 's'}
                {viewMode === 'week' ? ' this week' : ' today'}.
              </>
            ) : null}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          {opsView === 'schedule' ? (
            <>
              <Button type="button" onClick={() => setBookModalOpen(true)}>
                <CalendarPlus className="mr-2 h-4 w-4" />
                Book appointment
              </Button>

              <div className="inline-flex rounded-xl border bg-card p-1 shadow-sm">
                <Button
                  type="button"
                  size="sm"
                  variant={viewMode === 'day' ? 'default' : 'ghost'}
                  className={cn('rounded-lg', viewMode !== 'day' && 'text-muted-foreground')}
                  onClick={() => setViewMode('day')}
                >
                  Day
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={viewMode === 'week' ? 'default' : 'ghost'}
                  className={cn('rounded-lg', viewMode !== 'week' && 'text-muted-foreground')}
                  onClick={() => setViewMode('week')}
                >
                  Week
                </Button>
              </div>

              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setSelectedDate((current) => shiftIsoDate(current, -navStep))}
                aria-label={viewMode === 'week' ? 'Previous week' : 'Previous day'}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" onClick={() => setSelectedDate(todayIso())}>
                Today
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setSelectedDate((current) => shiftIsoDate(current, navStep))}
                aria-label={viewMode === 'week' ? 'Next week' : 'Next day'}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <label className="rounded-xl border bg-card px-3 py-2 shadow-sm">
                <span className="block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Go to date
                </span>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                  className="mt-0.5 w-full min-w-[10.5rem] bg-transparent text-sm font-semibold text-foreground"
                  aria-label={heading}
                />
              </label>
            </>
          ) : (
            <Button type="button" variant="outline" onClick={() => setOpsView('schedule')}>
              <CalendarPlus className="mr-2 h-4 w-4" />
              Open schedule
            </Button>
          )}
        </div>
      </div>

      <div className="sticky top-0 z-20 -mx-4 border-b border-border/60 bg-background/95 px-4 py-2.5 backdrop-blur supports-[backdrop-filter]:bg-background/80 lg:-mx-8 lg:px-8">
        <div
          className="inline-flex w-full max-w-md rounded-xl border bg-card p-1 shadow-sm sm:w-auto"
          role="tablist"
          aria-label="Calendar sections"
        >
          {OPS_VIEWS.map((view) => {
            const Icon = view.icon
            const isActive = opsView === view.id
            const badge =
              view.id === 'floor'
                ? arrivalsCount + queueEntries.length
                : view.id === 'schedule'
                  ? bookingCount
                  : 0

            return (
              <button
                key={view.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={cn(
                  'inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors sm:flex-none sm:px-4',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                )}
                onClick={() => setOpsView(view.id)}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {view.label}
                {badge > 0 ? (
                  <span
                    className={cn(
                      'min-w-[1.25rem] rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums',
                      isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-foreground',
                    )}
                  >
                    {badge > 99 ? '99+' : badge}
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {success}
        </div>
      ) : null}

      {opsView === 'floor' ? (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_300px]">
          <div className="space-y-4">
            <OperationsSettingsPanel />

            <ChairBoardGrid
              key={boardRefreshKey}
              compact
              queueEntries={queueEntries}
              onUpdated={handleBoardUpdated}
            />

            <CheckInArrivalsPanel
              appointments={appointments}
              chairs={chairs}
              selectedDate={arrivalsDate}
              onUpdated={handleCheckInUpdated}
            />
          </div>

          <WalkinQueuePanel
            stylists={activeStylists}
            entries={queueEntries}
            onEntriesChange={setQueueEntries}
            services={queueServices}
            queueLoading={queueLoading || metaLoading}
            onRefreshQueue={refreshQueue}
          />
        </div>
      ) : null}

      {opsView === 'schedule' ? (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_300px]">
          <section className="card-surface min-w-0 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
              <div>
                <h2 className="text-sm font-semibold text-foreground">
                  {viewMode === 'week' ? 'Week schedule' : 'Day schedule'}
                </h2>
                <p className="text-[11px] text-muted-foreground">
                  {viewMode === 'week'
                    ? 'Click a booking for details · click a day header for day view'
                    : `Click a booking for details · ${dayHours.open} – ${dayHours.close}`}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={rescheduleMode ? 'default' : 'outline'}
                  className={cn('rounded-lg text-xs', !rescheduleMode && 'text-muted-foreground')}
                  onClick={() => setRescheduleMode((current) => !current)}
                >
                  <Move className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  {rescheduleMode ? 'Drag mode on' : 'Drag to reschedule'}
                </Button>
                {appointmentsLoading ? (
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Updating…
                  </span>
                ) : null}
              </div>
            </div>

            <div className="max-h-[min(85vh,calc(100vh-11rem))] overflow-y-auto">
              {metaLoading ? (
                <div className="flex items-center justify-center gap-2 p-12 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading stylists…
                </div>
              ) : !calendarReady ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  {stylists.length > 0
                    ? 'No active stylists for this branch. Activate stylists in Masters → Stylists.'
                    : 'Add active stylists in Masters to display the calendar.'}
                </div>
              ) : viewMode === 'week' ? (
                <WeekCalendar
                  weekDates={weekRange.dates}
                  appointments={appointments}
                  stylists={activeStylists}
                  businessHours={businessHours}
                  openTime={weekGridHours.openTime}
                  closeTime={weekGridHours.closeTime}
                  slotMinutes={slotMinutes}
                  today={todayIso()}
                  onReschedule={handleReschedule}
                  onSelectAppointment={setSelectedAppointment}
                  rescheduleEnabled={rescheduleMode}
                  onSelectDay={openDayView}
                  reschedulingId={reschedulingId}
                />
              ) : (
                <DayCalendar
                  date={selectedDate}
                  appointments={appointments}
                  stylists={activeStylists}
                  openTime={dayHours.open}
                  closeTime={dayHours.close}
                  slotMinutes={slotMinutes}
                  onReschedule={handleReschedule}
                  onSelectAppointment={setSelectedAppointment}
                  rescheduleEnabled={rescheduleMode}
                  reschedulingId={reschedulingId}
                />
              )}
            </div>
          </section>

          <div className="space-y-4">
            <AppointmentLookupPanel
              selectedDate={selectedDate}
              onSelectDate={handleLookupDateSelect}
              onSelectAppointment={handleLookupAppointmentSelect}
              onBookDate={() => setBookModalOpen(true)}
            />

            <WalkinQueuePanel
              stylists={activeStylists}
              entries={queueEntries}
              onEntriesChange={setQueueEntries}
              services={queueServices}
              queueLoading={queueLoading || metaLoading}
              onRefreshQueue={refreshQueue}
            />
          </div>
        </div>
      ) : null}

      {opsView === 'blocks' ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setSelectedDate((current) => shiftIsoDate(current, -1))}
              aria-label="Previous day"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" onClick={() => setSelectedDate(todayIso())}>
              Today
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setSelectedDate((current) => shiftIsoDate(current, 1))}
              aria-label="Next day"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <div className="rounded-xl border bg-card px-4 py-2 text-sm font-semibold text-foreground shadow-sm">
              {formatDayHeading(selectedDate)}
            </div>
            <div className="inline-flex rounded-xl border bg-card p-1 shadow-sm">
              <Button
                type="button"
                size="sm"
                variant={viewMode === 'day' ? 'default' : 'ghost'}
                className={cn('rounded-lg', viewMode !== 'day' && 'text-muted-foreground')}
                onClick={() => setViewMode('day')}
              >
                Day
              </Button>
              <Button
                type="button"
                size="sm"
                variant={viewMode === 'week' ? 'default' : 'ghost'}
                className={cn('rounded-lg', viewMode !== 'week' && 'text-muted-foreground')}
                onClick={() => setViewMode('week')}
              >
                Week
              </Button>
            </div>
          </div>

          <BlockoutManager
            range={
              viewMode === 'week'
                ? { from: weekRange.from, to: weekRange.to }
                : { from: selectedDate, to: nextIsoDate(selectedDate) }
            }
            stylists={activeStylists}
            chairs={chairs}
            defaultDate={selectedDate}
          />
        </div>
      ) : null}

      <BookAppointmentModal
        open={bookModalOpen}
        onClose={() => setBookModalOpen(false)}
        selectedDate={selectedDate}
        openTime={viewMode === 'week' ? weekGridHours.openTime : dayHours.open}
        closeTime={viewMode === 'week' ? weekGridHours.closeTime : dayHours.close}
        slotMinutes={slotMinutes}
        stylists={activeStylists}
        onBooked={handleBooked}
      />

      <AppointmentDetailModal
        open={selectedAppointment !== null}
        appointment={selectedAppointment}
        stylists={activeStylists}
        openTime={viewMode === 'week' ? weekGridHours.openTime : dayHours.open}
        closeTime={viewMode === 'week' ? weekGridHours.closeTime : dayHours.close}
        slotMinutes={slotMinutes}
        onClose={() => setSelectedAppointment(null)}
        onReschedule={handleReschedule}
        onApproveOnline={async (appointmentId) => {
          await approveOnlineAppointment(appointmentId)
          await handleOnlineApproved()
          setSelectedAppointment(null)
        }}
        onOpenOutcome={setOutcomeAppointment}
        rescheduling={selectedAppointment !== null && reschedulingId === selectedAppointment.id}
      />

      <AppointmentOutcomeModal
        open={outcomeAppointment !== null}
        appointment={outcomeAppointment}
        onClose={() => setOutcomeAppointment(null)}
        onUpdated={handleOutcomeUpdated}
      />
    </div>
  )
}
